import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import API_BASE_URL from '../../components/apiconfig/api-config';
import SearchableDropdown from '../../components/common/SearchableDropdown';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CostBreakupForm = () => {
  const [formData, setFormData] = useState({
    purchase_contract_id: '',
    component_code: '',
    amount_inr: '',

    warehouse_entry_id: '',
    remarks: '',
    component_type: '',
  });

  const [componentOptions, setComponentOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [orderOptions, setOrderOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingComponents, setFetchingComponents] = useState(false);
  const [fetchingWarehouses, setFetchingWarehouses] = useState(false);
  const [fetchingOrders, setFetchingOrders] = useState(false);

  // Saved entries table
  const [savedEntries, setSavedEntries] = useState([]);

  const navigate = useNavigate();

  const FIELD_LIMITS = {
    purchase_contract_id: 10,
    component_code: 30,
    amount_inr: 14,

    warehouse_entry_id: 10,
    remarks: 1000,
    component_type: 50,
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace(/_/g, ' ')}`;
    }
    const requiredFields = ['purchase_contract_id', 'component_code', 'amount_inr'];
    if (requiredFields.includes(name) && (!value || value === '' || value === 0)) {
      error = `${name.replace(/_/g, ' ')} is required`;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) toast.error(error);
  };

  useEffect(() => {
    const fetchComponentCodes = async () => {
      setFetchingComponents(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-cost-component`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (res.ok) {
          setComponentOptions(data.data);
        } else {
          console.error('Failed to fetch component codes:', data.error);
        }
      } catch (err) {
        console.error('Error fetching component codes:', err);
      } finally {
        setFetchingComponents(false);
      }
    };

    const fetchWarehouseEntries = async () => {
      setFetchingWarehouses(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-entry`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (res.ok) {
          setWarehouseOptions(data.data);
        } else {
          console.error('Failed to fetch warehouse entries:', data.error);
        }
      } catch (err) {
        console.error('Error fetching warehouse entries:', err);
      } finally {
        setFetchingWarehouses(false);
      }
    };

    const fetchImportOrders = async () => {
      setFetchingOrders(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-purchase-contract`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (res.ok) {
          setOrderOptions(data.data);
        } else {
          console.error('Failed to fetch import orders:', data.error);
        }
      } catch (err) {
        console.error('Error fetching import orders:', err);
      } finally {
        setFetchingOrders(false);
      }
    };

    fetchComponentCodes();
    fetchWarehouseEntries();
    fetchImportOrders();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Helper: Purchase Order label dhundo
  const getOrderLabel = (id) => {
    const found = orderOptions.find(o => String(o.id) === String(id));
    return found ? `${found.id}, ${found.purchase_contract_no || 'N/A'}` : id;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    for (const key in formData) {
      if (FIELD_LIMITS[key]) {
        const error = validateField(key, formData[key]);
        if (error) {
          toast.error(error);
          setLoading(false);
          return;
        }
      }
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/cost/cost_breakup_post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (res.ok) {
        toast.success('Cost Breakup Created Successfully!');

        // Table mein entry add karo
        setSavedEntries(prev => [
          ...prev,
          {
            purchase_contract_id: formData.purchase_contract_id,
            purchase_label: getOrderLabel(formData.purchase_contract_id),
            component_code: formData.component_code,
            amount_inr: formData.amount_inr,

            warehouse_entry_id: formData.warehouse_entry_id,
            component_type: formData.component_type,
            remarks: formData.remarks,
          }
        ]);

        setFormData({
          purchase_contract_id: '',
          component_code: '',
          amount_inr: '',

          warehouse_entry_id: '',
          remarks: '',
          component_type: '',
        });
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-3 py-1.5">
        <h2 className="text-[15px] font-bold mb-0 text-left">Cost Breakup Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-2 py-0.5 rounded text-[12px] font-medium hover:bg-[#f0fdf4]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-3 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-medium mb-1">Purchase Contract Number</label>
              <SearchableDropdown
                options={orderOptions.map(order => ({
                  id: order.id,
                  name: `Purchase Order ID: ${order.id}, Contract: ${order.purchase_contract_no || 'N/A'}`
                }))}
                value={formData.purchase_contract_id}
                onChange={(value) => handleDropdownChange('purchase_contract_id', value)}
                onBlur={handleBlur}
                placeholder="Select an order"
                displayKey="name"
                valueKey="id"
                required
                disabled={fetchingOrders}
              />
              {fetchingOrders && (
                <p className="text-[12px] text-gray-500 mt-1">Loading orders...</p>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Component Code</label>
              <select
                name="component_code"
                value={formData.component_code}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.component_code}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
                disabled={fetchingComponents}
              >
                <option value="">Select a component</option>
                {componentOptions.map((component) => (
                  <option key={component.component_code} value={component.component_code}>
                    {component.component_code}
                  </option>
                ))}
              </select>
              {fetchingComponents && (
                <p className="text-[12px] text-gray-500 mt-1">Loading components...</p>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Amount (INR)</label>
              <input
                type="number"
                step="0.01"
                name="amount_inr"
                value={formData.amount_inr}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              />
            </div>



            <div>
              <label className="block text-[12px] font-medium mb-1">Warehouse Entry ID</label>
              <SearchableDropdown
                options={warehouseOptions.map(warehouse => ({
                  id: warehouse.warehouse_entry_id,
                  name: warehouse.display_name || warehouse.warehouse_entry_id.toString()
                }))}
                value={formData.warehouse_entry_id}
                onChange={(value) => handleDropdownChange('warehouse_entry_id', value)}
                onBlur={handleBlur}
                placeholder="Select a warehouse entry"
                displayKey="name"
                valueKey="id"
                disabled={fetchingWarehouses}
              />
              {fetchingWarehouses && (
                <p className="text-[12px] text-gray-500 mt-1">Loading warehouse entries...</p>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Component Type</label>
              <select
                name="component_type"
                value={formData.component_type}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
              >
                <option value="">Select Type (Optional)</option>
                <option value="Inbound">Inbound (Purchase)</option>
                <option value="Outbound">Outbound (Sale)</option>
                <option value="Internal">Internal Transfer</option>
                <option value="Vendor-Billed">Vendor-Billed</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-[12px] font-medium mb-1">Remarks</label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={FIELD_LIMITS.remarks}
              className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none resize-none"
              rows={3}
            ></textarea>
          </div>
        </div>

        <div className="px-3 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-3 py-1.5 text-[13px] bg-[#15803d] text-white font-semibold tracking-wide rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60"
            disabled={loading || fetchingComponents || fetchingWarehouses || fetchingOrders}
          >
            {loading ? 'Saving...' : 'Save Cost Breakup'}
          </button>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
        </div>
      </form>

      {/* Saved Entries Table */}
      {savedEntries.length > 0 && (
        <div className="px-3 pb-5">
          <div className="border border-gray-200 rounded">
            <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-[13px] font-semibold text-gray-700">Saved Cost Breakup Entries</h3>
              <span className="text-[11px] text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">{savedEntries.length} record{savedEntries.length > 1 ? 's' : ''}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-gray-100 text-gray-600">
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200 whitespace-nowrap">#</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200 whitespace-nowrap">Purchase Contract Number</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200 whitespace-nowrap">Component Code</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200 whitespace-nowrap">Amount (INR)</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200 whitespace-nowrap">Currency</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200 whitespace-nowrap">FX Rate</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200 whitespace-nowrap">Warehouse Entry ID</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200 whitespace-nowrap">Component Type</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200 whitespace-nowrap">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {savedEntries.map((entry, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-1.5 border-b border-gray-100 text-gray-500">{index + 1}</td>
                      <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800 whitespace-nowrap">{entry.purchase_label}</td>
                      <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">{entry.component_code || '-'}</td>
                      <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800 font-medium">
                        {entry.amount_inr ? `₹${Number(entry.amount_inr).toLocaleString('en-IN')}` : '-'}
                      </td>
                      <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">{entry.currency || '-'}</td>
                      <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">{entry.fx_rate || '-'}</td>
                      <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">{entry.warehouse_entry_id || '-'}</td>
                      <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">{entry.component_type || '-'}</td>
                      <td className="px-3 py-1.5 border-b border-gray-100 text-gray-500 max-w-[150px] truncate" title={entry.remarks}>{entry.remarks || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostBreakupForm;
